import { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Check, X, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { Container, Btn } from "@/components/kit";
import { Eyebrow } from "@/lib/anim";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/rasika-khode/15min";

export default function PaymentResult() {
  const loc = useLocation();
  const isCancel = loc.pathname.includes("cancel");
  const [state, setState] = useState(isCancel ? "cancelled" : "checking");
  const embedRef = useRef(null);

  // eslint-disable-next-line -- polls once per checkout session; API is a stable module const
  useEffect(() => {
    if (isCancel) return;
    const params = new URLSearchParams(loc.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setState("pending"); return; }
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { if (process.env.NODE_ENV === "development") console.debug("payment status poll retry", e); }
      setTimeout(poll, 2000);
    };
    poll();
  }, [isCancel, loc.search]);

  useEffect(() => {
    if (state !== "paid") return;
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch (e) { /* script already removed */ } };
  }, [state]);

  const meta = {
    checking: { icon: <Loader2 className="animate-spin" size={30} />, h: "Confirming your payment", p: "One moment while we confirm with Stripe.", ok: false },
    paid: { icon: <Check size={30} />, h: "You are booked in", p: "Payment confirmed and a receipt is on its way. Two things: pick a time below, and send any costing sheet, factory quote or sales export ahead so the session starts useful.", ok: true },
    pending: { icon: <Loader2 className="animate-spin" size={30} />, h: "Almost there", p: "The payment is still processing. You will get a confirmation email shortly.", ok: false },
    cancelled: { icon: <X size={30} />, h: "Checkout cancelled", p: "No payment was taken. Whenever you are ready, the engagement is one click away.", ok: false },
    error: { icon: <X size={30} />, h: "Something went wrong", p: "We could not confirm the payment. Please try again or email info@prox-au.com.", ok: false },
  }[state];

  return (
    <>
      <Seo title="Payment" canonical="/payment/success" />
      <main data-testid="payment-result" style={{ backgroundColor: "var(--bone)" }}>
        <Container size="md" className="py-24 md:py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-8" style={{ borderRadius: 2, background: meta.ok ? "var(--clay)" : "var(--sand)", color: meta.ok ? "var(--paper)" : "var(--ink)" }}>
              {meta.icon}
            </div>
            <Eyebrow>{meta.ok ? "Confirmed" : "Status"}</Eyebrow>
            <h1 className="t-display2" data-testid="payment-status-heading">{meta.h}</h1>
            <p className="t-lead mt-6 text-[var(--muted)]">{meta.p}</p>

            {state === "paid" && (
              <div className="mt-10">
                <div className="calendly-inline-widget" data-url={`${CALENDLY}?hide_gdpr_banner=1`} style={{ minWidth: 320, height: 700 }} data-testid="calendly-embed" ref={embedRef} />
                <p className="t-caption text-[var(--muted)] mt-3">If the scheduler does not load, <a href={CALENDLY} target="_blank" rel="noreferrer" className="lnk">pick a time here</a>.</p>
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-4">
              <Btn to="/" variant="outline">Back to home</Btn>
              {!meta.ok && state !== "checking" && <Btn to="/pricing" variant="clay">See pricing</Btn>}
            </div>
          </div>
        </Container>
      </main>
    </>
  );
}
